import React, { Component } from "react"

/* Components */
import { Card } from "@cuba-go/poker-ui"

/* utils */
import { get_unique_values } from "../constants/utils"


/* THE HANDS THAT ARE NOT LAID OUT IN TWO GROUPS

   Every other hand divides into the cards that make it and the cards that do not, and the
   server marks which are which. These two are marked as well, but what the mark means there
   is not a combination:

   FIVE PICTURES is every card a picture, so the whole hand is the combination. The three
   that come back marked are only its three highest, picked so that two of these hands can
   be told apart — a line drawn through them would be a line through nothing.

   NO COMBINATION has one card marked, and it is only the highest: what the round is decided
   on, not a combination the other four are shut out of. There is nothing to set apart, so
   the five are left exactly as they were dealt.

   The four of a kind is not here: four cards do make it, the fifth does not, and it is laid
   out four and one — see grouped(). */
const WHOLE_HANDS = ["FIVEPICTURES", "NOGAME"]


/* THE DEALER'S FIVE

   Dealt face up, the same as the three boxes: they come out of the same shoe, through the
   same scanner, and lie face up on the cloth, so there is nothing here to turn over. What
   waits for the end of the deal is the reading of the hand, not the sight of the cards —
   until it is read no card is marked, and once it is, the five fall into the two groups the
   hand is actually made of — see grouped(). That is the one time a dealt card moves, and it
   happens once: there is nothing here that runs, repeats, or keeps going afterwards. */
class DealerCards extends Component {

    /* The five as they were dealt, each marked with whether it makes the combination */
    laid = () => {

        const { cards, game } = this.props

        const list = cards ? get_unique_values(cards, "id") : []

        if (!game || !game.data) {
            return list.map(card => ({ ...card, status: false }))
        }

        return list.map(card => {
            const read = game.data.find(item => parseInt(item.id) === parseInt(card.id))
            return { ...card, status: !!(read && read.status) }
        })
    }

    /* HOW THE FIVE ARE GROUPED ONCE THE HAND IS READ

       A hand is not five cards. Usually it is three that make the bull and two that carry
       the points; on a four of a kind it is four and a fifth that has nothing to do with
       it. Which cards are which is the whole of what the player is looking for at the
       reveal, so the row is shown the way the hand is read: the ones that make it, a space,
       then the rest. The space is what says it — the cards are laid out as what they are,
       and nothing has to move or flash to point at them.

       The count is not fixed at three and two. Whatever the reading marks is one group and
       whatever it leaves is the other, so a four of a kind comes out four and one without
       this having to know anything about it.

       Two hands are not shown this way at all — see WHOLE_HANDS. And until the hand is read
       nothing is marked, so for the whole deal this returns the dealt order untouched and
       the row stays a plain row of five. */
    grouped = () => {

        const { game } = this.props

        const laid = this.laid()

        /* Nothing to set apart — see WHOLE_HANDS */
        if (game && WHOLE_HANDS.includes(game.code)) return { list: laid, split: 0 }

        const made = laid.filter(card => card.status)
        const rest = laid.filter(card => !card.status)

        /* A hand has to have two sides to be shown as two. One card on its own is never a
           combination in this game — only No Combination marks a single card — so this
           holds the row together even if a reading ever arrives without its code. */
        if (made.length < 2 || !rest.length) return { list: laid, split: 0 }

        return { list: [...made, ...rest], split: made.length }
    }

    /* Being part of the combination is a light on the card and nothing more. It used to be
       a 12px lift as well, with a bob running under it that had no end — so the reveal set
       the row moving and left it moving. See the note in dealer.css. */
    _card = card => (
        <div className={`niu--dealer-card ${card.status ? "niu--dealer-card-made" : ""}`} key={`d-${card.uid ? card.uid : card.id}`}>
            <Card data={card.name} />
        </div>
    )

    /* WHAT THE ROW IS CALLED ONCE THE HAND IS READ

       The same place the boxes say it: beside the name of the row, not in a pill hung under
       the cards. "Карты дилера" while they are coming, "Карты дилера Ню 4" from the moment
       the hand can be read — so the dealer's combination and the three boxes' are read off
       one line each, all at the same height, instead of the dealer's sitting somewhere of
       its own. */
    _name = () => {

        const { game } = this.props

        if (!game) return null

        /* No three of the five come to ten — there is no combination, only a high card */
        const none = parseInt(game.level) === 0 ? "niu--dealer-combination-none" : ""

        return <span className={`niu--dealer-combination ${none}`}>{game.name}</span>
    }

    render = () => {

        const { style } = this.props

        const { list, split } = this.grouped()

        return (
            <div className={`niu--dealer ${style ? style : ""}`}>
                <h2>Карты дилера{this._name()}</h2>
                <div className="niu--dealer-box">

                    {/* Five places, held open from the first card so the row does not grow
                        out of the middle of the table as the deal goes round.

                        They are held to the end of the round as well: the dealer has five
                        cards and never any other number, so a row that swapped its places
                        for bare cards at the reveal only put every card in it a couple of
                        pixels to one side, which on a centred row of five is the whole row
                        stepping sideways under the player's eye.

                        A place is keyed by the card standing in it and not by where it sits
                        in the row. That is what lets the five be regrouped at the reveal
                        without any of them being torn down and built again — they are the
                        same five nodes moved, so the deal-in that plays when a card lands
                        does not play a second time over the whole row. */}
                    <div className="niu--dealer-cards">
                        {Array.from(Array(5).keys()).map(i => {

                            const card = list[i]

                            /* The one place that opens the space between the two groups */
                            const apart = split && i === split ? "niu--dealer-card-place-split" : ""

                            return (
                                <div
                                    className={`niu--dealer-card-place ${card ? "niu--dealer-card-place-filled" : ""} ${apart}`}
                                    key={card ? `card-${card.uid ? card.uid : card.id}` : `empty-${i}`}
                                >
                                    {card ? this._card(card) : null}
                                </div>
                            )
                        })}
                    </div>

                </div>
            </div>
        )
    }

}

export default DealerCards
